/**
 * SHAP Waterfall Chart Component
 * 
 * Visualizes step-by-step contribution of features to final prediction.
 * Shows how each feature pushes the prediction up or down from base value.
 * 
 * Following BRAIN-DESIGN-SYSTEM.md:
 * - Uses SecurityColors constants
 * - Green for positive contributions
 * - Red for negative contributions
 * - Proper opacity patterns (/20 for backgrounds)
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { ArrowRight } from 'lucide-react';

interface WaterfallChartProps {
  baseValue: number;
  features: Array<{
    name: string;
    value: any;
    contribution: number;
  }>;
  finalPrediction: number;
}

export const WaterfallChart: React.FC<WaterfallChartProps> = ({
  baseValue,
  features,
  finalPrediction
}) => {
  const chartData = useMemo(() => {
    let cumulative = baseValue;
    const data = [];
    
    data.push({
      name: 'Base Value',
      value: baseValue,
      cumulative: baseValue,
      isBase: true
    });
    
    features.forEach((feature) => {
      const prevCumulative = cumulative;
      cumulative += feature.contribution;
      
      data.push({
        name: feature.name,
        value: feature.contribution,
        cumulative: cumulative,
        prevCumulative: prevCumulative,
        featureValue: feature.value,
        isPositive: feature.contribution > 0
      });
    });
    
    data.push({
      name: 'Final',
      value: finalPrediction,
      cumulative: finalPrediction,
      isFinal: true
    });
    
    return data;
  }, [baseValue, features, finalPrediction]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'text.primary' }}>
          SHAP Waterfall Chart
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Base Value
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {baseValue.toFixed(3)}
          </Typography>
        </Box>
        <ArrowRight className="text-gray-400" size={20} style={{ marginTop: '8px' }} />
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Final Prediction
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {finalPrediction.toFixed(3)}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Total Change
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: (finalPrediction - baseValue) > 0 ? '#22C55E' : '#EF4444',
              fontWeight: 600 
            }}
          >
            {(finalPrediction - baseValue > 0 ? '+' : '')}{(finalPrediction - baseValue).toFixed(3)}
          </Typography>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis 
            dataKey="name" 
            angle={-45} 
            textAnchor="end" 
            height={100}
            tick={{ fill: '#9CA3AF', fontSize: 12 }}
          />
          <YAxis tick={{ fill: '#9CA3AF' }} />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#1F2937', 
              border: '1px solid #374151',
              borderRadius: '4px'
            }}
            labelStyle={{ color: '#F8FAFC' }}
            itemStyle={{ color: '#F8FAFC' }}
            formatter={(value: any, name: string, props: any) => {
              if (props.payload.featureValue !== undefined) {
                return [
                  `${value.toFixed(3)} (value: ${props.payload.featureValue})`,
                  'Contribution'
                ];
              }
              return [value.toFixed(3), 'Value'];
            }}
          />
          <Legend wrapperStyle={{ color: '#9CA3AF' }} />
          <ReferenceLine y={0} stroke="#6B7280" strokeDasharray="3 3" />
          <Bar dataKey="value" name="Contribution">
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`}
                fill={
                  entry.isBase || entry.isFinal ? '#3B82F6' :
                  entry.isPositive ? '#22C55E' : '#EF4444'
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <Box sx={{ mt: 2, display: 'flex', gap: 3, justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#22C55E', borderRadius: 0.5 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Positive Impact
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#EF4444', borderRadius: 0.5 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Negative Impact
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#3B82F6', borderRadius: 0.5 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Base/Final
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default WaterfallChart;
